import { createTheme } from '@mui/material/styles';

/**
 * Devias / Minimal-style theme for ezPICRA
 * Primary brand: orange #F58220
 */
const theme = createTheme({
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 900,
      lg: 1200,
      xl: 1440,
    },
  },
  palette: {
    mode: 'light',
    primary: {
      main: '#F58220',
      light: '#FF9A4A',
      dark: '#E6710D',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#64748b',
      light: '#94a3b8',
      dark: '#475569',
      contrastText: '#ffffff',
    },
    background: {
      default: '#f8fafc',
      paper: '#ffffff',
    },
    text: {
      primary: '#1a202c',
      secondary: '#4a5568',
      disabled: '#94a3b8',
    },
    divider: '#e2e8f0',
    success: {
      main: '#10b981',
    },
    warning: {
      main: '#f59e0b',
    },
    error: {
      main: '#ef4444',
    },
    info: {
      main: '#3b82f6',
    },
    neutral: {
      50: '#f8fafc',
      100: '#f1f5f9',
      200: '#e2e8f0',
      300: '#cbd5e1',
      400: '#94a3b8',
      500: '#64748b',
      600: '#475569',
      700: '#334155',
      800: '#1e293b',
      900: '#0f172a',
    },
  },
  typography: {
    fontFamily:
      '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif',
    h1: { fontWeight: 700, fontSize: '2.25rem', lineHeight: 1.2 },
    h2: { fontWeight: 700, fontSize: '1.875rem', lineHeight: 1.3 },
    h3: { fontWeight: 700, fontSize: '1.5rem', lineHeight: 1.35 },
    h4: { fontWeight: 700, fontSize: '1.25rem', lineHeight: 1.4 },
    h5: { fontWeight: 600, fontSize: '1.125rem', lineHeight: 1.4 },
    h6: { fontWeight: 600, fontSize: '1rem', lineHeight: 1.5 },
    subtitle1: { fontSize: '1rem', fontWeight: 500 },
    subtitle2: { fontSize: '0.875rem', fontWeight: 500 },
    body1: { fontSize: '1rem', lineHeight: 1.6 },
    body2: { fontSize: '0.875rem', lineHeight: 1.57 },
    button: { fontWeight: 600, textTransform: 'none' },
    caption: { fontSize: '0.75rem', lineHeight: 1.5 },
    overline: {
      fontSize: '0.75rem',
      fontWeight: 600,
      letterSpacing: '0.08em',
      textTransform: 'uppercase',
    },
  },
  shape: {
    borderRadius: 8,
  },
  shadows: [
    'none',
    '0px 1px 2px rgba(15, 23, 42, 0.05)',
    '0px 1px 3px rgba(15, 23, 42, 0.08), 0px 1px 2px rgba(15, 23, 42, 0.04)',
    '0px 4px 6px -1px rgba(15, 23, 42, 0.08), 0px 2px 4px -1px rgba(15, 23, 42, 0.04)',
    '0px 10px 15px -3px rgba(15, 23, 42, 0.08), 0px 4px 6px -2px rgba(15, 23, 42, 0.04)',
    '0px 20px 25px -5px rgba(15, 23, 42, 0.08), 0px 10px 10px -5px rgba(15, 23, 42, 0.03)',
    '0px 25px 50px -12px rgba(15, 23, 42, 0.2)',
    '0px 25px 50px -12px rgba(15, 23, 42, 0.2)',
    '0px 25px 50px -12px rgba(15, 23, 42, 0.2)',
    '0px 25px 50px -12px rgba(15, 23, 42, 0.2)',
    '0px 25px 50px -12px rgba(15, 23, 42, 0.2)',
    '0px 25px 50px -12px rgba(15, 23, 42, 0.2)',
    '0px 25px 50px -12px rgba(15, 23, 42, 0.2)',
    '0px 25px 50px -12px rgba(15, 23, 42, 0.2)',
    '0px 25px 50px -12px rgba(15, 23, 42, 0.2)',
    '0px 25px 50px -12px rgba(15, 23, 42, 0.2)',
    '0px 25px 50px -12px rgba(15, 23, 42, 0.2)',
    '0px 25px 50px -12px rgba(15, 23, 42, 0.2)',
    '0px 25px 50px -12px rgba(15, 23, 42, 0.2)',
    '0px 25px 50px -12px rgba(15, 23, 42, 0.2)',
    '0px 25px 50px -12px rgba(15, 23, 42, 0.2)',
    '0px 25px 50px -12px rgba(15, 23, 42, 0.2)',
    '0px 25px 50px -12px rgba(15, 23, 42, 0.2)',
    '0px 25px 50px -12px rgba(15, 23, 42, 0.2)',
    '0px 25px 50px -12px rgba(15, 23, 42, 0.2)',
  ],
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          boxShadow: 'none',
          '&:hover': {
            boxShadow: 'none',
          },
        },
        containedPrimary: {
          background: 'linear-gradient(135deg, #F58220 0%, #E6710D 100%)',
          '&:hover': {
            background: 'linear-gradient(135deg, #E6710D 0%, #D7610A 100%)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          border: '1px solid #e2e8f0',
          boxShadow: '0px 1px 3px rgba(15, 23, 42, 0.08)',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          borderRight: '1px solid #e2e8f0',
          backgroundColor: '#ffffff',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: 'rgba(255, 255, 255, 0.9)',
          color: '#1a202c',
          boxShadow: 'none',
          borderBottom: '1px solid #e2e8f0',
          backdropFilter: 'blur(8px)',
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          marginBottom: 4,
          '&.Mui-selected': {
            backgroundColor: 'rgba(245, 130, 32, 0.12)',
            color: '#E6710D',
            '& .MuiListItemIcon-root': {
              color: '#E6710D',
            },
            '&:hover': {
              backgroundColor: 'rgba(245, 130, 32, 0.16)',
            },
          },
          '&:hover': {
            backgroundColor: '#f1f5f9',
          },
        },
      },
    },
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: '#f8fafc',
        },
      },
    },
  },
});

export default theme;
